/**
 * Infinite Loop Detector
 * Detects and prevents infinite loops in scripts via iteration counting
 */

export interface LoopDetectionConfig {
  maxIterations: number; // Maximum loop iterations allowed per script
  enableBackgroundChecks?: boolean; // Enable periodic checks for stuck scripts
}

/**
 * Detects infinite loops by instrumenting code with iteration counters
 * Injects counter checks into common loop patterns
 */
export class InfiniteLoopDetector {
  private config: LoopDetectionConfig;

  constructor(config?: LoopDetectionConfig) {
    this.config = {
      maxIterations: config?.maxIterations ?? 10000,
      enableBackgroundChecks: config?.enableBackgroundChecks ?? false,
    };
  }

  /**
   * Instrument code to detect infinite loops
   * Adds iteration counters to while, for, and do-while loops
   * Note: This is a best-effort approach and may not catch all infinite loops
   * Uses a unique counter variable name to minimize collision risk
   */
  instrumentCode(code: string): string {
    // Use a counter name that's unlikely to collide with user variables
    const counterVar = '__formControlLoopCounter_' + Math.random().toString(36).substr(2, 9) + '__';
    const checkCode = `if (++${counterVar} > ${this.config.maxIterations}) throw new Error('Infinite loop detected: exceeded ${this.config.maxIterations} iterations');`;

    let instrumented = code;

    // Inject global counter initialization at the start
    instrumented = `let ${counterVar} = 0;\n${instrumented}`;

    // Instrument while loops: while (...) → while (...) { CHECK; ...
    // Only matches while with opening brace on same line
    instrumented = instrumented.replace(
      /while\s*\([^)]*\)\s*\{/g,
      (match) => {
        return `${match} ${checkCode}`;
      }
    );

    // Instrument for loops: for (...;...;...) → for (...;...;...) { CHECK; ...
    // Only matches for with opening brace on same line
    instrumented = instrumented.replace(
      /for\s*\([^)]*\)\s*\{/g,
      (match) => {
        return `${match} ${checkCode}`;
      }
    );

    // Instrument do-while: do { CHECK; ... } while (...)
    instrumented = instrumented.replace(
      /do\s*\{/g,
      `do { ${checkCode}`
    );

    return instrumented;
  }

  /**
   * Check if code contains obvious infinite loop patterns
   * Returns true if suspicious patterns detected (but not guaranteed infinite)
   */
  hasInfiniteSuspicion(code: string): boolean {
    // Check for while(true)
    if (/while\s*\(\s*true\s*\)/.test(code)) {
      return true;
    }

    // Check for while(1)
    if (/while\s*\(\s*1\s*\)/.test(code)) {
      return true;
    }

    // Check for while with no condition updates
    // This is a basic heuristic and not perfect
    const whileMatches = code.match(/while\s*\(\s*(.+?)\s*\)\s*\{([^}]*)\}/g);
    if (whileMatches) {
      for (const match of whileMatches) {
        const condition = match.match(/while\s*\(\s*(.+?)\s*\)/)?.[1] || '';
        const body = match.match(/\{([^}]*)\}$/)?.[1] || '';

        // If condition uses a variable and body doesn't modify it, suspicious
        const conditionVars = condition.match(/\b[a-zA-Z_]\w*\b/g) || [];
        const bodyText = body.toLowerCase();

        for (const varName of conditionVars) {
          if (!bodyText.includes(varName)) {
            // Variable in condition but never modified in body
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<LoopDetectionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current max iterations setting
   */
  getMaxIterations(): number {
    return this.config.maxIterations;
  }
}
